import React, { useEffect, useState } from 'react';
import tipoPersona from "../../../../mocks/tipoPersona.json";
import { ciudadesSelect, Participante } from '../../models/participantes';
import { useParticipantes } from '../../hooks/useParticipantes';
import * as Yup from "yup";
import validaRut from '../../../../utils/validaRut';
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from 'react-hook-form';
import { enumTipoParticipanteConValores, enumTipoPersona, StatusCode, TipoFuncionalidad } from '../../../../utils/enums';
import { useToast } from '../../../../hooks/useToast';
import Button from '../../../../components/inputs/yup/Button';
import Validaciones from '../../../../utils/validaciones';
import { DeudorJuridico } from '../../models/deudorJuridico';
import { format } from 'rut.js';
import { useDeudor } from '../../hooks/useDeudor';
import { AxiosResponse } from 'axios';
import { getListaParticipantes } from '../../services/servicesParticipantes';
import ConfirmationModal from '../../../../components/ui/materialui/confirmationModal';
import MButton from '../../../../components/inputs/yup/materialui/mButton';
import InputSelect from '../../../../components/inputs/yup/materialui/inputSelect';
import InputRut from '../../../../components/inputs/yup/materialui/inputRut';
import InputText from '../../../../components/inputs/yup/materialui/inputText';
import InputNumber from '../../../../components/inputs/yup/materialui/inputNumber';
import { Card, CardContent, Divider, Grid2, Typography } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import SaveAltIcon from '@mui/icons-material/SaveAlt';
import CloseIcon from '@mui/icons-material/Close';
import { ListResponseDto } from '../../../../dtos/ListResponseDto';
import { useDetalleMutuoPermissions } from '../../hooks/useDetalleMutuoPermissions';
import { DetalleMutuoPermissionGuard } from '../../../../components/auth/DetalleMutuoPermissionGuard';

interface IProps {
    dataDeudorJuridico: DeudorJuridico | undefined;
    ciudadesSelect: ciudadesSelect[];
    idMutuo: number;
    esMutuoNormalyConComplementario: boolean;
    setShowParticipantes: React.Dispatch<React.SetStateAction<boolean>>;
    renderizar: () => void;
    volver: () => void;
    isComplementario: boolean;
    isDesactivado?: boolean;
    esVigente?: boolean;
}


const DeudorJuridicoForm: React.FC<IProps> = (props) => {

    const { comunaPorId, comunasSelect, comunasCorrespondecia, setComunasSelect, setComunasCorrespondecia,
        listaParticipantePorId } = useParticipantes();
    const { actualizarDeudorJuridico } = useDeudor();

    const [isEditing, setIsEditing] = useState<boolean>(false);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [toggleRutOriginal, setToggleRutOriginal] = useState<boolean>(false);
    const [participantesCache, setParticipantesCache] = useState<Participante[]>([]);
    const [modalConfirmacion, setModalConfirmacion] = useState({
        show: false,
        title: '',
        message: '',
        metodo: () => {
        },
    });

    const schemaDeudor = Yup.object<DeudorJuridico>().shape({
        tipodeudor: Yup.mixed<enumTipoPersona>().oneOf(Object.values(enumTipoPersona)).required(),
        rut: Yup.string()
            .default('')
            .nonNullable("Campo requerido")
            .test('valida-rut', 'RUT inválido', function (value) {
                return validaRut.rutFormat(value);
            })
            .required("Campo requerido")
            .test({
                name: 'validaciones-rut-unico',
                exclusive: false,
                params: {},
                message: 'RUT debe ser distinto al del resto de los participantes',
                test: async (value: string, { createError }) => {

                    let participantes: Participante[] = [];

                    if (participantesCache.length == 0) {
                        let resp: AxiosResponse<ListResponseDto<Participante>> = await getListaParticipantes(props.idMutuo);

                        if (resp.status == StatusCode.Ok) {
                            setParticipantesCache(await resp.data.items);
                        }
                        else {
                            return createError({ message: "No fué posible validar el RUT" })
                        }
                    }

                    participantes = participantesCache;

                    let rutIngresado: string = format(value, { dots: false });

                    let rutPermitido: boolean = true;
                    participantes?.forEach((participante: Participante) => {

                        if (participante.idTipoParticipante === enumTipoParticipanteConValores.Codeudor) {
                            if (participante.rut === rutIngresado) {
                                rutPermitido = false;
                            }
                        }

                    });

                    if (!rutPermitido)
                        return createError({ message: "RUT no permitido, pertenece a un codeudor" })

                    rutPermitido = true;
                    participantes?.forEach((participante: Participante) => {

                        if (participante.idTipoParticipante === enumTipoParticipanteConValores.Mandatario) {
                            if (participante.rut === rutIngresado) {
                                rutPermitido = false;
                            }
                        }

                    });

                    if (!rutPermitido)
                        return createError({ message: "RUT no permitido, pertenece a un mandatario" })

                    return true;
                }
            }),

        rutOriginal: Yup.string()
            .nonNullable()
            .required('Campo requerido'),

        razonSocial: Yup.string()
            .required("Campo requerido")
            .max(50, "Máximo 50 caractéres")
            .test('valida-nombre', 'Caractéres inválidos', function (value) {
                return Validaciones.soloLetrasYPuntuacion(value ?? '')
            }),

        telefonoOficina: Yup.number()
            .transform((value) => Number.isNaN(value) ? null : value)
            .notRequired()
            .max(999999999, "Máximo 9 digitos"),

        telefonoCelular: Yup.number()
            .transform((value) => Number.isNaN(value) ? null : value)
            .notRequired()
            .max(999999999, "Máximo 9 digitos"),

        telefono: Yup.number()
            .transform((value) => Number.isNaN(value) ? null : value)
            .notRequired()
            .max(999999999, "Máximo 9 digitos"),

        email: Yup.string().required("Campo requerido")
            .max(50, "Máximo 50 caractéres")
            .test('valida-email', 'Email inválido', function (value) {
                return Validaciones.esValidoEmail(value ?? '');
            }),

        direccionLegal: Yup.string()
            .required("Campo requerido")
            .max(100, "Máximo 100 caractéres")
            .test('valida-direccion', 'Caractéres inválidos', function (value) {
                return Validaciones.isDireccion(value ?? '')
            }),

        ciudadId: Yup.string()
            .required("Campo requerido"),

        comunaId: Yup.string()
            .required("Campo requerido"),

        direccionCorrespondencia: Yup.string()
            .required("Campo requerido")
            .max(100, "Máximo 100 caractéres")
            .test('valida-direccion-correspondencia', 'Caractéres inválidos', function (value) {
                return Validaciones.isDireccion(value ?? '')
            }),

        ciudadCorrespondenciaId: Yup.string().required("Campo requerido"),

        comunaCorrespondenciaId: Yup.string().required("Campo requerido"),

        porcentajedominio: Yup.number().required("Campo requerido")
            .transform((value) => Number.isNaN(value) ? null : value)
            .min(0, "Valor debe ser mayor o igual a 0")
            .test({
                name: 'valida-max-100',
                exclusive: false,
                params: {},
                message: 'Valor no puede superar el 100%',
                test: (value, context) => value === undefined ||
                    value === null ||
                    value <= 100
            })
            .test({
                name: 'valida-max-dominio',
                exclusive: false,
                params: {},
                message: 'Este porcentaje de dominio, sumado al de los codeudores debe ser 100%',
                test: async (value: number, { createError }) => {

                    let participantes: Participante[] = [];

                    if (participantesCache.length == 0) {
                        let resp: AxiosResponse<ListResponseDto<Participante>> = await getListaParticipantes(props.idMutuo);

                        if (resp.status == StatusCode.Ok) {
                            setParticipantesCache(await resp.data.items);
                        }
                        else {
                            return createError({ message: "No fué posible validar el porcentaje de dominio" })
                        }
                    }

                    participantes = participantesCache;

                    let dominioCodeudores: number = 0;
                    participantes?.forEach((participante: Participante) => {

                        if (participante.idTipoParticipante === enumTipoParticipanteConValores.Codeudor) {
                            dominioCodeudores = dominioCodeudores + participante.porcentajeDominio;
                        }

                    });

                    return (value === (100 - dominioCodeudores))
                }
            }),
    });

    const {
        formState: { errors, isDirty, isValid },
        handleSubmit,
        control,
        setValue,
        watch,
        reset,
        trigger
    } = useForm<DeudorJuridico>({
        defaultValues: props.dataDeudorJuridico,
        mode: "all",
        reValidateMode: "onChange",
        resolver: yupResolver(schemaDeudor)
    })

    const { addToast } = useToast();
    const { permisoCarteraInfo, permisoCarteraContacto } = useDetalleMutuoPermissions(props.esVigente);

    const dataFormDeudor = watch();

    useEffect(() => {
        if (props.dataDeudorJuridico?.ciudadId)
            comunaPorId(props.dataDeudorJuridico?.ciudadId);

        if (props.dataDeudorJuridico?.ciudadCorrespondenciaId)
            comunaPorId(props.dataDeudorJuridico?.ciudadCorrespondenciaId, true);

        if (props.dataDeudorJuridico) {
            reset(props.dataDeudorJuridico);
        }


    }, [props.dataDeudorJuridico, reset])

    useEffect(() => {

        setValue("rutOriginal", format(dataFormDeudor.rut, { dots: false }));

    }, [toggleRutOriginal])

    const handleCiudadComuna = async (id: string | null, esCorrespondencia: boolean) => {

        if (esCorrespondencia) {
            setValue('comunaCorrespondenciaId', '');
            setComunasCorrespondecia([]);
        } else {
            setValue('comunaId', '');
            setComunasSelect([]);
        }

        trigger("comunaId");
        trigger("comunaCorrespondenciaId");

        if (id) {
            await comunaPorId(id, esCorrespondencia);
        }
    }

    const handleCancelarEdicionClick = () => {

        if (isDirty) {
            setModalConfirmacion({
                show: true,
                title: "Hay cambios sin guardar",
                message: "¿Desea abandonar la edición?",
                metodo: () => {
                    setIsEditing(false);
                    reset();
                },
            });
        }
        else {
            setIsEditing(false);
            reset();
        }

    }

    const onSubmit = async (data: DeudorJuridico) => {

        data.rut = format(data.rut, { dots: false });

        setIsSubmitting(true);

        try {
            await actualizarDeudorJuridico(data);

            addToast({
                mostrar: true,
                titulo: 'Confirmación',
                tipo: 'Success',
                mensaje: 'Datos modificados correctamente'
            });

            setIsEditing(false);
            setIsSubmitting(false);

            if (format(dataFormDeudor.rut, { dots: false }) != dataFormDeudor.rutOriginal) {
                setToggleRutOriginal(!toggleRutOriginal);
            }

            reset(dataFormDeudor);
            props.renderizar();
        }
        catch (error) {
            console.log(error);
            setIsSubmitting(false);
        }

    };
    const isReadonlyWithPermissionContacto = () => {
        // Si NO es vigente (Bandeja), NO valida permisos
        if (!props.esVigente) return !isEditing;

        // Si ES vigente (Cartera), valida permiso Cartera_editar_contacto
        return !isEditing || !permisoCarteraContacto;
    }
    const isReadonlyWithPermission = () => {
        // Si NO es vigente (Bandeja), NO valida permisos
        if (!props.esVigente) return !isEditing;

        // Si ES vigente (Cartera), valida permiso
        return !isEditing || !permisoCarteraInfo;
    }
    return (
        <>

            <ConfirmationModal
                onHide={() =>
                    setModalConfirmacion({
                        ...modalConfirmacion,
                        show: false,
                    })
                }
                onConfirm={modalConfirmacion.metodo}
                show={modalConfirmacion.show}
                title={modalConfirmacion.title}
                message={modalConfirmacion.message}
            />

            <form onSubmit={handleSubmit(onSubmit)}>

                {!isEditing && <>

                    <Grid2 container alignItems="center" spacing={2}>

                        <Grid2 size={2}>
                            <h3>Deudor</h3>
                        </Grid2>
                        <DetalleMutuoPermissionGuard
                            esVigente={props.esVigente}
                            permissions={[TipoFuncionalidad.Bandeja_editar, TipoFuncionalidad.Cartera_editar_info, TipoFuncionalidad.Cartera_editar_contacto]}>
                            <Grid2 container spacing={1} size={10} sx={{ textAlign: "right", justifyContent: 'flex-end' }}>
                                {!props.isComplementario && (
                                    <MButton
                                        variant="contained"
                                        startIcon={<EditIcon />}
                                        disabled={props.isDesactivado}
                                        onClick={() => { setIsEditing(true); trigger(); }}>
                                        Editar
                                    </MButton>
                                )}

                                <MButton
                                    variant="contained"
                                    className="btn btn-sani"
                                    onClick={() => { props.setShowParticipantes(true); setIsEditing(false); props.volver(); }}
                                >
                                    Listado participantes
                                </MButton>
                            </Grid2>
                        </DetalleMutuoPermissionGuard>

                    </Grid2>

                </>
                }

                {isEditing && <>

                    <Grid2 container alignItems="center" spacing={2}>

                        <Grid2 size={2}>
                            <h3>Deudor</h3>
                        </Grid2>
                        <DetalleMutuoPermissionGuard
                            esVigente={props.esVigente}
                            permissions={[TipoFuncionalidad.Bandeja_editar, TipoFuncionalidad.Cartera_editar_info, TipoFuncionalidad.Cartera_editar_contacto]}>
                            <Grid2 container spacing={1} size={10} sx={{ textAlign: "right", justifyContent: 'flex-end' }}>
                                <MButton
                                    variant='contained'
                                    color='primary' disabled={!(isDirty && isValid) || isSubmitting}
                                    startIcon={<SaveAltIcon />}
                                    onClick={handleSubmit(onSubmit)}>
                                    Guardar
                                </MButton>

                                <MButton
                                    variant='contained'
                                    color={'inherit'}
                                    startIcon={<CloseIcon />}
                                    onClick={() => { handleCancelarEdicionClick(); }}>
                                    Cancelar
                                </MButton>
                            </Grid2>
                        </DetalleMutuoPermissionGuard>

                    </Grid2>
                </>
                }
                <Divider sx={{ my: 2 }} />
                <Grid2 sx={{ mt: 2, mb: 3 }}>

                    <Card sx={{ mb: 2, backgroundColor: 'white' }} raised>
                        <CardContent>
                            <Typography className='card-title' variant="h6" gutterBottom>
                                Datos persona jurídica
                            </Typography>
                            <Grid2 container spacing={2} mt={2}>
                                <Grid2 size={{ xs: 12, lg: 3 }}>
                                    <InputSelect
                                        label="Tipo persona"
                                        name="tipodeudor"
                                        control={control}
                                        options={tipoPersona}
                                        error={errors.tipodeudor}
                                        disabled={isReadonlyWithPermission()}
                                    />
                                </Grid2>
                                <Grid2 size={{ xs: 12, lg: 3 }}>
                                    {/* <input {...control} type="hidden" name="rutOriginal" /> */}
                                    <InputRut
                                        label="RUT"
                                        name="rut"
                                        control={control}
                                        error={errors.rut}
                                        readonly
                                    />
                                </Grid2>
                                <Grid2 size={{ xs: 12, lg: 3 }}>
                                    <InputText
                                        label="Razón social"
                                        name="razonSocial"
                                        control={control}
                                        error={errors.razonSocial}
                                        readonly={isReadonlyWithPermission()}
                                    />
                                </Grid2>
                            </Grid2>
                        </CardContent>
                    </Card>

                    <Card sx={{ mb: 2, backgroundColor: 'white' }} raised>
                        <CardContent>
                            <Typography className='card-title' variant="h6" gutterBottom>
                                Contacto
                            </Typography>
                            <Grid2 container spacing={2} mt={2}>
                                <Grid2 size={{ xs: 12, lg: 3 }}>
                                    <InputNumber
                                        label="Teléfono oficina"
                                        name="telefonoOficina"
                                        control={control}
                                        readonly={isReadonlyWithPermissionContacto()}
                                        minValue={0}
                                        maxValue={999999999}
                                        prefix="+56"
                                        error={errors.telefonoCelular}
                                    />
                                </Grid2>
                                <Grid2 size={{ xs: 12, lg: 3 }}>
                                    <InputNumber
                                        label="Teléfono celular"
                                        name="telefonoCelular"
                                        control={control}
                                        readonly={isReadonlyWithPermissionContacto()}
                                        minValue={0}
                                        maxValue={999999999}
                                        prefix="+56"
                                        error={errors.telefonoCelular}
                                    />
                                </Grid2>
                                <Grid2 size={{ xs: 12, lg: 3 }}>
                                    <InputNumber
                                        label="Teléfono"
                                        name="telefono"
                                        control={control}
                                        readonly={isReadonlyWithPermissionContacto()}
                                        minValue={0}
                                        maxValue={999999999}
                                        prefix="+56"
                                        error={errors.telefono}
                                    />
                                </Grid2>
                                <Grid2 size={{ xs: 12, lg: 3 }}>
                                    <InputText
                                        label="Email"
                                        name="email"
                                        control={control}
                                        error={errors.email}
                                        readonly={isReadonlyWithPermissionContacto()}
                                    />
                                </Grid2>
                            </Grid2>
                        </CardContent>
                    </Card>

                    <Card sx={{ mb: 2, backgroundColor: 'white' }} raised>
                        <CardContent>
                            <Typography className='card-title' variant="h6" gutterBottom>
                                Dirección comercial
                            </Typography>
                            <Grid2 container spacing={2} mt={2}>
                                <Grid2 size={{ xs: 12, lg: 3 }}>
                                    <InputText
                                        label="Calle"
                                        name="direccionLegal"
                                        control={control}
                                        error={errors.direccionLegal}
                                        readonly={isReadonlyWithPermission()}
                                    />
                                </Grid2>
                                <Grid2 size={{ xs: 12, lg: 3 }}>
                                    <InputSelect
                                        label="Ciudad"
                                        name="ciudadId"
                                        control={control}
                                        options={props.ciudadesSelect}
                                        error={errors.ciudadId}
                                        disabled={isReadonlyWithPermission()}
                                        onChange={(value) => handleCiudadComuna(value ?? '', false)}
                                    />
                                </Grid2>
                                <Grid2 size={{ xs: 12, lg: 3 }}>
                                    <InputSelect
                                        label="Comuna"
                                        name="comunaId"
                                        control={control}
                                        options={comunasSelect}
                                        error={errors.comunaId}
                                        disabled={isReadonlyWithPermission()}
                                    />
                                </Grid2>
                            </Grid2>

                            <Typography className='card-title' variant="h6" gutterBottom sx={{ mt: 3 }}>
                                Dirección envío correspondencia
                            </Typography>
                            <Grid2 container spacing={2} mt={2}>
                                <Grid2 size={{ xs: 12, lg: 3 }}>
                                    <InputText
                                        label="Calle"
                                        name="direccionCorrespondencia"
                                        control={control}
                                        error={errors.direccionCorrespondencia}
                                        readonly={isReadonlyWithPermissionContacto()}
                                    />
                                </Grid2>
                                <Grid2 size={{ xs: 12, lg: 3 }}>
                                    <InputSelect
                                        label="Ciudad"
                                        name="ciudadCorrespondenciaId"
                                        control={control}
                                        options={props.ciudadesSelect}
                                        error={errors.ciudadCorrespondenciaId}
                                        onChange={(value) => handleCiudadComuna(value, true)}
                                        disabled={isReadonlyWithPermissionContacto()}
                                    />
                                </Grid2>
                                <Grid2 size={{ xs: 12, lg: 3 }}>
                                    <InputSelect
                                        label="Comuna"
                                        name="comunaCorrespondenciaId"
                                        control={control}
                                        options={comunasCorrespondecia}
                                        error={errors.comunaCorrespondenciaId}
                                        disabled={isReadonlyWithPermissionContacto()}
                                    />
                                </Grid2>
                            </Grid2>
                        </CardContent>
                    </Card>


                    <Card sx={{ mb: 2, backgroundColor: 'white' }} raised>
                        <CardContent>
                            <Typography className='card-title' variant="h6" gutterBottom>
                                Datos deuda
                            </Typography>
                            <Grid2 container spacing={2} mt={2}>
                                <Grid2 size={{ xs: 12, lg: 3 }}>
                                    <InputNumber
                                        label="Porcentaje dominio"
                                        name="porcentajedominio"
                                        control={control}
                                        readonly={isReadonlyWithPermission()}
                                        minValue={0}
                                        maxValue={100}
                                        suffix='%'
                                        error={errors.porcentajedominio}
                                    />

                                </Grid2>
                            </Grid2>
                        </CardContent>
                    </Card>

                </Grid2>
            </form >

        </>
    );
};

export default DeudorJuridicoForm;



